/* eslint-disable prefer-promise-reject-errors */
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { url } from 'constants/api.constants';
import refreshTokenId from 'utils/firebase.util';

// sets the server api url, as the base url for all requests
// made using this axios instance
const axiosInstance = axios.create({
  baseURL: url.API_URL,
});

// requests will be json by default
axiosInstance.defaults.headers.post['Content-Type'] = 'application/json';

const API_ERROR_LOG_STYLE = 'color:#c62828;font-weight:600';

function formatApiErrorTimestamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate(),
  )} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function logDevApiErrorLine(message: string): void {
  // eslint-disable-next-line no-console
  console.log(`%c${message}`, API_ERROR_LOG_STYLE);
}

function truncateForLog(s: string, max = 2000): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

function formatRequestBodyForLog(data: unknown): string {
  if (data == null) return '';
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    return '[FormData]';
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return `[Blob ${data.type || 'application/octet-stream'} ${
      data.size
    }b]`;
  }
  if (typeof data === 'string') {
    return truncateForLog(data);
  }
  try {
    return truncateForLog(JSON.stringify(data));
  } catch {
    return '[unserializable body]';
  }
}

export type RosieAxiosRequestConfig = AxiosRequestConfig & {
  /** Skip dev-console error line for expected 404 responses (e.g. optional resource GET). */
  suppress404Log?: boolean;
  /** Skip dev-console error line for expected 403 (e.g. Bagel before auth is ready). */
  suppress403Log?: boolean;
};

function logDevApiError(error: AxiosError): void {
  if (process.env.NODE_ENV === 'production') return;
  if (axios.isCancel(error)) return;

  const cfg = error.config as RosieAxiosRequestConfig | undefined;
  const status = error.response?.status;
  if (cfg?.suppress404Log && status === 404) {
    return;
  }
  if (cfg?.suppress403Log && status === 403) {
    return;
  }
  const method = (cfg?.method ?? 'GET').toUpperCase();
  let fullUrl = '(unknown URL)';
  if (cfg) {
    try {
      fullUrl = axios.getUri(cfg);
    } catch {
      const base = cfg.baseURL ?? '';
      const path = cfg.url ?? '';
      fullUrl = path.startsWith('http')
        ? path
        : `${base.replace(/\/$/, '')}${
            path.startsWith('/') ? path : `/${path}`
          }`;
    }
  }

  const ts = formatApiErrorTimestamp();
  const requestLine = `"${method} ${fullUrl} HTTP/1.1"`;

  if (error.response) {
    const { statusText } = error.response;
    const statusTextPart = statusText ? ` ${statusText}` : '';
    logDevApiErrorLine(
      `${ts} ❌ RESPONSE: ${
        status ?? ''
      }${statusTextPart} — incoming request ${requestLine}`,
    );
  } else {
    logDevApiErrorLine(
      `${ts} ❌ RESPONSE: no response — incoming request ${requestLine}`,
    );
  }

  if (
    cfg &&
    !['GET', 'HEAD'].includes(method) &&
    cfg.data != null &&
    cfg.data !== ''
  ) {
    logDevApiErrorLine(
      `${ts}    └ body: ${formatRequestBodyForLog(cfg.data)}`,
    );
  }
}

axiosInstance.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    logDevApiError(error);
    return Promise.reject(error);
  },
);

export default axiosInstance;

/**
 * Makes request for refresh firebase auth token
 * Sets updated refreshed token as axios header
 * for server validation
 */
const setUpdatedToken = async () => {
  try {
    // req updated user id token
    const token = await refreshTokenId();
    // set token in header
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
    return Promise.resolve('done');
  } catch (e) {
    return Promise.reject({
      status: 400,
      errorMessage:
        'An error occurred with Firebase refresh token. Please refresh the page and try again.',
    });
  }
};

/**
 * Makes get request
 * @param url
 * @returns
 * - api request payload
 */
export const makeGetRequest = async <T = any>(
  reqUrl: string,
  config: RosieAxiosRequestConfig = {},
): Promise<AxiosResponse<T>> => {
  await setUpdatedToken();
  return axiosInstance.get<T>(reqUrl, config);
};

export const makeGetRequestBlob = async (
  reqUrl: string,
): Promise<AxiosResponse<Blob>> => {
  await setUpdatedToken();
  return axiosInstance.get<Blob>(reqUrl, {
    responseType: 'blob',
  });
};

/**
 * Makes post request
 * @param url
 * @param payload
 * @returns
 */
export const makePostRequest = async <T = any>(
  reqUrl: string,
  payload: unknown,
  config: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> => {
  await setUpdatedToken();
  return axiosInstance.post<T>(reqUrl, payload, config);
};

/**
 * Makes put request
 * @param url
 * @param payload
 * @returns
 */
export const makePutRequest = async <T = any>(
  reqUrl: string,
  payload: unknown,
  config: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> => {
  await setUpdatedToken();
  return axiosInstance.put<T>(reqUrl, payload, config);
};

/**
 * Makes delete request
 * @param url
 * @param payload
 * @returns
 */
export const makeDeleteRequest = async <T = any>(
  reqUrl: string,
): Promise<AxiosResponse<T>> => {
  await setUpdatedToken();
  return axiosInstance.delete<T>(reqUrl);
};

/**
 * Makes patch request
 * @param reqUrl
 * @param payload
 * @returns
 */
export const makePatchRequest = async <T = any>(
  reqUrl: string,
  payload: unknown,
  config: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> => {
  await setUpdatedToken();
  return axiosInstance.patch<T>(reqUrl, payload, config);
};
